/** Store → customer detail: profile (editable), addresses (add/edit/delete), note/tags, custom fields, orders. */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Checkbox, Col, Descriptions, Empty, Form, Input, Modal, Popconfirm, Result, Row, Space, Spin, Switch, Table, Tag, Typography, message } from 'antd';
import TagsInput from '../../../shared/components/TagsInput';
import { ArrowLeftOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { StatusTag, fmtDate, listOf, money, sitePath, toastErr, useSite } from './common';
import { Address } from './OrderDetail';
import { orderName } from './Orders';
import { CountrySelect } from './ShopCommon';
import CustomFieldsCard from './CustomFieldsCard';

const { Text } = Typography;

const ADDR_KEYS = ['name', 'company', 'line1', 'line2', 'city', 'region', 'postal_code', 'country', 'phone'];

/** One address (customer address book): {name, company, line1, line2, city, region, postal_code, country, phone}. */
const AddressModal = ({ value, onClose, onSave }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const v = await form.validateFields();
        setBusy(true);
        try { await onSave(Object.fromEntries(ADDR_KEYS.map((k) => [k, (v[k] || '').trim()]))); } finally { setBusy(false); }
    };
    const f = (k, label, span = 12, req = false) => (
        <Col xs={24} sm={span}><Form.Item name={k} label={label} rules={req ? [{ required: true }] : []} style={{ marginBottom: 8 }}><Input /></Form.Item></Col>
    );
    return (
        <Modal open={!!value} title={value?.i == null ? t('Add address') : t('Edit address')} onCancel={onClose} onOk={submit} confirmLoading={busy} destroyOnClose>
            <Form form={form} layout="vertical" preserve={false} initialValues={Object.fromEntries(ADDR_KEYS.map((k) => [k, value?.a?.[k] || undefined]))}>
                <Row gutter={12}>
                    {f('name', t('Name'), 12, true)}
                    {f('company', t('Company'))}
                    {f('line1', t('Address'), 24, true)}
                    {f('line2', t('Apartment, suite, etc.'), 24)}
                    {f('city', t('City'), 8, true)}
                    {f('region', t('State / province'), 8)}
                    {f('postal_code', t('Postal code'), 8)}
                    <Col xs={24} sm={12}><Form.Item name="country" label={t('Country')} rules={[{ required: true }]} style={{ marginBottom: 8 }}><CountrySelect style={{ width: '100%' }} /></Form.Item></Col>
                    {f('phone', t('Phone'))}
                </Row>
            </Form>
        </Modal>
    );
};

const CustomerDetail = () => {
    const { t } = useTranslation();
    const { cid } = useParams();
    const navigate = useNavigate();
    const { api, siteId, currency, shop } = useSite();
    const [c, setC] = useState(null);
    const [err, setErr] = useState(null);

    const [note, setNote] = useState('');
    const [tags, setTags] = useState([]);
    const [saving, setSaving] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [eraseOpen, setEraseOpen] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState('');
    const [erasing, setErasing] = useState(false);
    const [openOrders, setOpenOrders] = useState(false);
    const [force, setForce] = useState(false);
    const [addr, setAddr] = useState(null); // {i, a} being edited
    const [contactOpen, setContactOpen] = useState(false);
    const [contactForm] = Form.useForm();
    const exportData = async () => {
        setExporting(true);
        try { await api.download(`/customers/${cid}/export`, `customer-${cid}.zip`); } catch (e) { toastErr(e); } finally { setExporting(false); }
    };
    const erase = async () => {
        setErasing(true);
        try {
            await api.post(`/customers/${cid}/erase`, { confirm: confirmEmail, force });
            message.success(t('Customer deleted'));
            navigate(sitePath(siteId, 'customers'));
        } catch (e) {
            if (e.body?.code === 'open_orders') setOpenOrders(true);
            toastErr(e);
        } finally { setErasing(false); }
    };
    const apply = (d) => { const x = d?.customer ? { ...d.customer, orders: d.orders ?? d.customer.orders } : d; setC(x); setNote(x?.note || ''); setTags(listOf(x?.tags)); };
    useEffect(() => {
        let live = true;
        api.get(`/customers/${cid}`).then((d) => live && apply(d)).catch((e) => live && setErr(e));
        return () => { live = false; };
    }, [api, cid]);
    const update = async (patch) => {
        setSaving(true);
        try { apply(await api.put(`/customers/${cid}`, patch)); message.success(t('Saved')); } catch (e) { toastErr(e); } finally { setSaving(false); }
    };

    const saveAddresses = async (list) => {
        try { apply(await api.put(`/customers/${cid}`, { addresses: list })); message.success(t('Saved')); return true; } catch (e) { toastErr(e); return false; }
    };
    const saveContact = async () => {
        const v = await contactForm.validateFields();
        setSaving(true);
        try { apply(await api.put(`/customers/${cid}`, v)); message.success(t('Saved')); setContactOpen(false); } catch (e) { toastErr(e); } finally { setSaving(false); }
    };

    if (err) return <Result status={err.status === 404 ? '404' : 'error'} title={err.status === 404 ? t('Customer not found') : (err.error || t('Failed to load'))} extra={<Button onClick={() => navigate(sitePath(siteId, 'customers'))}>{t('Customers')}</Button>} />;
    if (!c) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
    const orders = listOf(c.orders);
    const addresses = listOf(c.addresses);

    return (
        <div>
            <Space style={{ marginBottom: 12 }}>
                <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(sitePath(siteId, 'customers'))} />
                <Text strong style={{ fontSize: 18 }}>{c.name || c.email}</Text>
                {c.state === 'disabled' && <Tag>{t('Disabled')}</Tag>}
            </Space>
            <Row gutter={16}>
                <Col xs={24} lg={16}>
                    <Card style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={8}><Text type="secondary">{t('Amount spent')}</Text><div style={{ fontSize: 20 }}>{money(c.total_spent || 0, currency)}</div></Col>
                            <Col span={8}><Text type="secondary">{t('Orders')}</Text><div style={{ fontSize: 20 }}>{c.orders_count ?? orders.length}</div></Col>
                            <Col span={8}><Text type="secondary">{t('Customer since')}</Text><div style={{ fontSize: 14, marginTop: 6 }}>{fmtDate(c.created_at, false)}</div></Col>
                        </Row>
                    </Card>
                    <Card title={t('Orders')} style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
                        <Table size="small" rowKey="id" dataSource={orders} pagination={false} scroll={{ x: 560 }}
                            onRow={(o) => ({ onClick: () => navigate(sitePath(siteId, `orders/${o.id}`)), style: { cursor: 'pointer' } })}
                            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No orders yet')} /> }}
                            columns={[
                                { title: t('Order', { context: 'shop' }), key: 'n', render: (_, o) => <a>{orderName(o, shop)}</a> },
                                { title: t('Date'), dataIndex: 'created_at', render: (v) => fmtDate(v) },
                                { title: t('Payment'), dataIndex: 'financial_status', render: (v) => <StatusTag value={v} /> },
                                { title: t('Fulfillment'), dataIndex: 'fulfillment_status', render: (v) => <StatusTag value={v} /> },
                                { title: t('Total', { context: 'shop' }), key: 't', align: 'right', render: (_, o) => money(o.total, o.currency || currency) },
                            ]} />
                    </Card>
                </Col>
                <Col xs={24} lg={8}>
                    <Card title={t('Contact')} size="small" style={{ marginBottom: 16 }}
                        extra={<Button size="small" type="text" icon={<EditOutlined />} onClick={() => { contactForm.setFieldsValue({ name: c.name || '', email: c.email || '', phone: c.phone || '', accepts_marketing: !!c.accepts_marketing }); setContactOpen(true); }} />}>
                        <Descriptions column={1} size="small">
                            {c.name && <Descriptions.Item label={t('Name')}>{c.name}</Descriptions.Item>}
                            <Descriptions.Item label={t('Email', { context: 'shop' })}><a href={`mailto:${c.email}`}>{c.email}</a>{c.email_verified && <Tag color="green" style={{ marginLeft: 6 }}>{t('Verified')}</Tag>}</Descriptions.Item>
                            {c.phone && <Descriptions.Item label={t('Phone')}>{c.phone}</Descriptions.Item>}
                            <Descriptions.Item label={t('Marketing')}>{c.accepts_marketing ? t('Subscribed') : t('Not subscribed')}</Descriptions.Item>
                            {c.lang && <Descriptions.Item label={t('Language')}>{c.lang}</Descriptions.Item>}
                            {c.last_login_at && <Descriptions.Item label={t('Last login')}>{fmtDate(c.last_login_at)}</Descriptions.Item>}
                        </Descriptions>
                    </Card>
                    <Card title={t('Addresses')} size="small" style={{ marginBottom: 16 }}
                        extra={<Button size="small" type="text" icon={<PlusOutlined />} disabled={addresses.length >= 20} onClick={() => setAddr({ i: null, a: {} })} />}>
                        {addresses.length ? addresses.map((a, i) => (
                            <div key={i} style={{ marginBottom: 10, display: 'flex', gap: 8 }}>
                                <div style={{ flex: 1 }}>{i === 0 && addresses.length > 1 && <Tag style={{ marginBottom: 4 }}>{t('Default')}</Tag>}<Address a={a} /></div>
                                <Space direction="vertical" size={0}>
                                    <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setAddr({ i, a })} />
                                    <Popconfirm title={t('Delete this address?')} onConfirm={() => saveAddresses(addresses.filter((_, j) => j !== i))}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm>
                                    {i > 0 && <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }} onClick={() => saveAddresses([a, ...addresses.filter((_, j) => j !== i)])}>{t('Make default')}</Button>}
                                </Space>
                            </div>
                        )) : <Text type="secondary">-</Text>}
                    </Card>
                    <CustomFieldsCard owner="customer" oid={c.id} />
                    <Card title={t('Notes')} size="small" style={{ marginBottom: 16 }}>
                        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} autoSize={{ minRows: 3 }} placeholder={t('Only visible to staff')} style={{ marginBottom: 8 }} />
                        <TagsInput format="array" spaceSeparates={false} value={tags} onChange={setTags} placeholder={t('Tags')} />
                        {(note !== (c.note || '') || JSON.stringify(tags) !== JSON.stringify(listOf(c.tags))) && (
                            <Button size="small" type="primary" style={{ marginTop: 8 }} loading={saving} onClick={() => update({ note, tags })}>{t('Save')}</Button>
                        )}
                    </Card>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Switch checked={c.state !== 'disabled'} onChange={(v) => update({ state: v ? 'enabled' : 'disabled' })} checkedChildren={t('Account enabled')} unCheckedChildren={t('Account disabled')} />
                        <Button block icon={<DownloadOutlined />} loading={exporting} onClick={exportData}>{t('Export data')}</Button>
                        <Button block danger icon={<DeleteOutlined />} onClick={() => { setConfirmEmail(''); setForce(false); setOpenOrders(false); setEraseOpen(true); }}>{t('Delete / anonymize')}</Button>
                    </Space>
                </Col>
            </Row>
            <AddressModal value={addr} onClose={() => setAddr(null)} onSave={async (a) => {
                const list = addr.i == null ? [...addresses, a] : addresses.map((x, j) => (j === addr.i ? a : x));
                if (await saveAddresses(list)) setAddr(null);
            }} />
            <Modal open={contactOpen} title={t('Edit contact')} onCancel={() => setContactOpen(false)} onOk={saveContact} confirmLoading={saving}>
                <Form form={contactForm} layout="vertical">
                    <Form.Item name="name" label={t('Name')}><Input maxLength={191} /></Form.Item>
                    <Form.Item name="email" label={t('Email', { context: 'shop' })} rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
                    <Form.Item name="phone" label={t('Phone')}><Input maxLength={64} /></Form.Item>
                    <Form.Item name="accepts_marketing" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox>{t('Agreed to receive marketing emails')}</Checkbox></Form.Item>
                </Form>
            </Modal>
            <Modal open={eraseOpen} title={t('Delete / anonymize')} onCancel={() => setEraseOpen(false)} onOk={erase} confirmLoading={erasing} destroyOnHidden
                okText={t('Delete')} okButtonProps={{ danger: true, disabled: confirmEmail.trim().toLowerCase() !== String(c.email).toLowerCase() || (openOrders && !force) }}>
                <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t('The account is deleted and orders stay without personal data. This cannot be undone.')} />
                <Input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={c.email} />
                {openOrders && <Checkbox checked={force} onChange={(e) => setForce(e.target.checked)} style={{ marginTop: 12 }}>{t('Anonymize open orders too')}</Checkbox>}
            </Modal>
        </div>
    );
};

export default CustomerDetail;
